import { useState, useEffect } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { CssBaseline, ThemeProvider, useTheme, useMediaQuery } from "@mui/material";
import { ColorModeContext, useMode } from "shared/theme";
import Sidebar, { SIDEBAR_WIDTH_PX } from "shared/layout/Sidebar";
import Topbar from "shared/layout/Topbar";
import { Toaster } from "react-hot-toast";
import AppRoutes from "shared/routes/AppRoutes";
import { useAuth } from "shared/hooks/useAuth";
import { isTenantPublicEntryPath } from "shared/utils/tenantRoute";

/** Routes that never show the app sidebar (login + other unauthenticated entry points). */
const PUBLIC_SHELL_PATHS = [
  "/",
  "/login",
  "/login/:tenantId",
  "/resetPassword",
  "/resetPassword/:tenantId",
  "/:tenantKey",
  "/:tenantKey/first-login/change-password",
  "/first-login/change-password",
  "/register",
  "/notFound",
  "/:tenantKey/notFound",
  "/home",
  "/reportForm",
  "/incidentForm/:id",
  "/incidentDetails/:id",
];

function normalizePathname(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function isPublicShellPath(pathname) {
  const p = normalizePathname(pathname);
  return (
    PUBLIC_SHELL_PATHS.some((pattern) => matchPath({ path: pattern, end: true }, p)) ||
    isTenantPublicEntryPath(p)
  );
}

function AppLayout() {
  const muiTheme = useTheme();
  const isDesktopSidebar = useMediaQuery(muiTheme.breakpoints.up("md"));
  /** Closed on small screens by default; open whenever viewport is md+. */
  const [isSidebar, setIsSidebar] = useState(false);

  useEffect(() => {
    setIsSidebar(isDesktopSidebar);
  }, [isDesktopSidebar]);

  const location = useLocation();
  const { user, sessionLoading } = useAuth();
  const profile = user?.data;
  const authenticatedShell =
    Boolean(profile) && !sessionLoading && !isPublicShellPath(location.pathname);

  const showToolbar =
    Boolean(profile) && !sessionLoading && !isPublicShellPath(location.pathname);

  return (
    <div className="app">
      {authenticatedShell && <Sidebar isSidebarOpen={isSidebar} setIsSidebarOpen={setIsSidebar} />}

      <main
        className="content"
        style={
          authenticatedShell && isDesktopSidebar
            ? { marginLeft: SIDEBAR_WIDTH_PX }
            : undefined
        }
      >
        {showToolbar && <Topbar setIsSidebarOpen={setIsSidebar} />}
        <AppRoutes />
      </main>
    </div>
  );
}

function App() {
  const { user } = useAuth();
  const tenantBranding = user?.data?.tenantBranding ?? null;
  const [theme, colorMode] = useMode(tenantBranding);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster position="bottom-left" reverseOrder={false} />
        <AppLayout />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
